/**
 * Interface translations only. Publication text is never translated here —
 * that happens through the translation service and is stored separately.
 */

export const en = {
  "nav.discover": "Discover",
  "nav.categories": "Categories",
  "nav.following": "Following",
  "nav.library": "Library",
  "nav.about": "About",
  "nav.write": "Write",
  "nav.studio": "Creator Studio",
  "nav.startWriting": "Start writing",
  "nav.signIn": "Sign in",
  "nav.join": "Join Quibblies",
  "nav.signOut": "Sign out",
  "nav.settings": "Settings",
  "nav.search": "Search publications",

  "common.save": "Save",
  "common.cancel": "Cancel",
  "common.close": "Close",
  "common.loading": "Loading…",
  "common.all": "All",
  "common.optional": "Optional",

  "status.draft": "Draft",
  "status.submitted": "Submitted",
  "status.under_review": "Under review",
  "status.changes_requested": "Changes requested",
  "status.approved": "Approved",
  "status.rejected": "Rejected",
  "status.published": "Published",

  "settings.title": "Language & Region",
  "settings.subtitle":
    "Quibblies is one global community. These settings change your experience — they never move you to a separate site.",
  "settings.interfaceLanguage": "Interface language",
  "settings.currency": "Preferred currency",
  "settings.timezone": "Timezone",
  "settings.country": "Country or region",
  "settings.countryHelp": "Private. Never shown on your public profile.",
  "settings.contentLanguages": "Preferred content languages",
  "settings.onlyMyLanguages": "Show only publications in my languages",
  "settings.includeTranslatable": "Include publications that can be translated",

  "tips.button": "Tip Writer",
  "tips.support": "Support {name}",
  "tips.custom": "Custom",
  "tips.customAmount": "Custom amount",
  "tips.message": "Add a short message",
  "tips.messagePlaceholder": "Really enjoyed this.",
  "tips.anonymous": "Tip anonymously",
  "tips.anonymousHelp": "The writer will see “Anonymous Reader” instead of your username.",
  "tips.total": "You are tipping",
  "tips.fee": "Quibblies takes a 5% transaction fee",
  "tips.writerReceives": "Writer receives",
  "tips.confirm": "Continue to payment",
  "tips.minimum": "Minimum tip is {amount}.",
  "tips.optionalNotice": "Tipping is always optional. Publications are never paywalled.",
  "tips.pendingNotice":
    "Payments are not live yet. Your tip is recorded as pending and will be completed once Quibblies' payment provider is active.",
  "tips.thanks": "Thank you — your support has been recorded.",

  "translate.button": "Translate",
  "translate.to": "Translate to {language}",
  "translate.machineNotice": "Automatically translated from {language}.",
  "translate.notAuthor": "This translation was produced by machine, not by the writer.",
  "translate.viewOriginal": "View original",
  "translate.originalLanguage": "Original language",
  "translate.stale": "The writer has updated this publication since this translation was made.",
  "translate.working": "Translating…",

  "discover.language": "Language",
  "discover.allLanguages": "All languages",
  "writer.languages": "Languages I write in",
  "publication.language": "Publication language",
} as const;

export type TranslationKey = keyof typeof en;
type Dict = Partial<Record<TranslationKey, string>>;

export const es: Dict = {
  "nav.discover": "Descubrir",
  "nav.categories": "Categorías",
  "nav.following": "Siguiendo",
  "nav.library": "Biblioteca",
  "nav.about": "Acerca de",
  "nav.write": "Escribir",
  "nav.studio": "Estudio de creación",
  "nav.startWriting": "Empezar a escribir",
  "nav.signIn": "Iniciar sesión",
  "nav.join": "Únete a Quibblies",
  "nav.signOut": "Cerrar sesión",
  "nav.settings": "Ajustes",
  "nav.search": "Buscar publicaciones",
  "common.save": "Guardar",
  "common.cancel": "Cancelar",
  "common.close": "Cerrar",
  "common.loading": "Cargando…",
  "common.all": "Todo",
  "status.draft": "Borrador",
  "status.submitted": "Enviado",
  "status.under_review": "En revisión",
  "status.changes_requested": "Cambios solicitados",
  "status.approved": "Aprobado",
  "status.rejected": "Rechazado",
  "status.published": "Publicado",
  "settings.title": "Idioma y región",
  "settings.interfaceLanguage": "Idioma de la interfaz",
  "settings.currency": "Moneda preferida",
  "settings.timezone": "Zona horaria",
  "settings.country": "País o región",
  "settings.contentLanguages": "Idiomas de lectura preferidos",
  "settings.onlyMyLanguages": "Mostrar solo publicaciones en mis idiomas",
  "settings.includeTranslatable": "Incluir publicaciones que se pueden traducir",
  "tips.button": "Apoyar al escritor",
  "tips.support": "Apoyar a {name}",
  "tips.custom": "Personalizado",
  "tips.message": "Añade un mensaje breve",
  "tips.anonymous": "Apoyar de forma anónima",
  "tips.total": "Vas a aportar",
  "tips.fee": "Quibblies aplica una comisión del 5%",
  "tips.writerReceives": "El escritor recibe",
  "tips.confirm": "Continuar al pago",
  "translate.button": "Traducir",
  "translate.to": "Traducir al {language}",
  "translate.machineNotice": "Traducido automáticamente del {language}.",
  "translate.viewOriginal": "Ver original",
  "translate.originalLanguage": "Idioma original",
  "discover.language": "Idioma",
  "discover.allLanguages": "Todos los idiomas",
  "writer.languages": "Idiomas en los que escribo",
  "publication.language": "Idioma de la publicación",
};

export const fr: Dict = {
  "nav.discover": "Découvrir",
  "nav.categories": "Catégories",
  "nav.following": "Abonnements",
  "nav.library": "Bibliothèque",
  "nav.about": "À propos",
  "nav.write": "Écrire",
  "nav.studio": "Studio de création",
  "nav.startWriting": "Commencer à écrire",
  "nav.signIn": "Se connecter",
  "nav.join": "Rejoindre Quibblies",
  "nav.signOut": "Se déconnecter",
  "nav.settings": "Paramètres",
  "nav.search": "Rechercher des publications",
  "common.save": "Enregistrer",
  "common.cancel": "Annuler",
  "common.loading": "Chargement…",
  "status.draft": "Brouillon",
  "status.under_review": "En cours d'examen",
  "status.published": "Publié",
  "settings.title": "Langue et région",
  "settings.interfaceLanguage": "Langue de l'interface",
  "settings.currency": "Devise préférée",
  "settings.contentLanguages": "Langues de lecture préférées",
  "tips.button": "Soutenir l'auteur",
  "tips.support": "Soutenir {name}",
  "tips.custom": "Montant libre",
  "tips.anonymous": "Soutenir anonymement",
  "tips.fee": "Quibblies prélève une commission de 5 %",
  "tips.writerReceives": "L'auteur reçoit",
  "tips.confirm": "Continuer vers le paiement",
  "translate.button": "Traduire",
  "translate.to": "Traduire en {language}",
  "translate.machineNotice": "Traduit automatiquement depuis le {language}.",
  "translate.viewOriginal": "Voir l'original",
  "discover.language": "Langue",
  "discover.allLanguages": "Toutes les langues",
  "writer.languages": "Langues dans lesquelles j'écris",
  "publication.language": "Langue de la publication",
};

export const de: Dict = {
  "nav.discover": "Entdecken",
  "nav.categories": "Kategorien",
  "nav.following": "Gefolgt",
  "nav.library": "Bibliothek",
  "nav.about": "Über uns",
  "nav.write": "Schreiben",
  "nav.studio": "Creator Studio",
  "nav.startWriting": "Mit dem Schreiben beginnen",
  "nav.signIn": "Anmelden",
  "nav.join": "Quibblies beitreten",
  "nav.signOut": "Abmelden",
  "nav.settings": "Einstellungen",
  "nav.search": "Veröffentlichungen suchen",
  "common.save": "Speichern",
  "common.cancel": "Abbrechen",
  "settings.title": "Sprache & Region",
  "settings.interfaceLanguage": "Sprache der Oberfläche",
  "settings.currency": "Bevorzugte Währung",
  "settings.contentLanguages": "Bevorzugte Lesesprachen",
  "tips.button": "Autor unterstützen",
  "tips.support": "{name} unterstützen",
  "tips.anonymous": "Anonym unterstützen",
  "tips.fee": "Quibblies erhebt eine Gebühr von 5 %",
  "tips.writerReceives": "Der Autor erhält",
  "translate.button": "Übersetzen",
  "translate.to": "Ins {language} übersetzen",
  "translate.machineNotice": "Automatisch aus dem {language} übersetzt.",
  "translate.viewOriginal": "Original ansehen",
  "discover.language": "Sprache",
  "discover.allLanguages": "Alle Sprachen",
  "writer.languages": "Sprachen, in denen ich schreibe",
  "publication.language": "Sprache der Veröffentlichung",
};

export const it: Dict = {
  "nav.discover": "Esplora",
  "nav.categories": "Categorie",
  "nav.library": "Libreria",
  "nav.write": "Scrivi",
  "nav.settings": "Impostazioni",
  "settings.title": "Lingua e regione",
  "settings.interfaceLanguage": "Lingua dell'interfaccia",
  "settings.currency": "Valuta preferita",
  "tips.button": "Sostieni l'autore",
  "tips.fee": "Quibblies applica una commissione del 5%",
  "translate.button": "Traduci",
  "translate.viewOriginal": "Vedi originale",
  "discover.language": "Lingua",
  "discover.allLanguages": "Tutte le lingue",
  "publication.language": "Lingua della pubblicazione",
};

export const pt: Dict = {
  "nav.discover": "Descobrir",
  "nav.categories": "Categorias",
  "nav.library": "Biblioteca",
  "nav.write": "Escrever",
  "nav.settings": "Definições",
  "settings.title": "Idioma e região",
  "settings.interfaceLanguage": "Idioma da interface",
  "settings.currency": "Moeda preferida",
  "tips.button": "Apoiar o autor",
  "tips.fee": "A Quibblies cobra uma taxa de 5%",
  "translate.button": "Traduzir",
  "translate.viewOriginal": "Ver original",
  "discover.language": "Idioma",
  "discover.allLanguages": "Todos os idiomas",
  "publication.language": "Idioma da publicação",
};

export const DICTIONARIES: Record<string, Dict> = { en, es, fr, de, it, pt };
